import { useCallback, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
} from "@nav-games/games-core";
import { SAIL_PARTS, type SailPart } from "@nav-games/sailing";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";
import { SailPartsFigure } from "../../ui/SailPartsFigure";

interface PartsProps {
  onNavigate: (to: string) => void;
}

const MODULE = "sailing";
const GAME = "parts";
const CARDS_PER_SESSION = 10;
const CHOICES_PER_CARD = 4;

interface Card {
  part: SailPart;
  choices: readonly SailPart[];
}

function randChoice<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]!;
}

function buildCard(): Card {
  const part = randChoice(SAIL_PARTS);
  const pool = SAIL_PARTS.filter((p) => p.id !== part.id);
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = pool[i]!;
    pool[i] = pool[j]!;
    pool[j] = a;
  }
  const choices: SailPart[] = [part, ...pool.slice(0, CHOICES_PER_CARD - 1)];
  for (let i = choices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = choices[i]!;
    choices[i] = choices[j]!;
    choices[j] = a;
  }
  return { part, choices };
}

export function Parts({ onNavigate }: PartsProps) {
  const recorder = useMemo(
    () => new SessionRecorder({ module: MODULE, game: GAME, length: CARDS_PER_SESSION }),
    [],
  );

  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => buildCard());
  const [shownAt, setShownAt] = useState(() => Date.now());
  const [chosen, setChosen] = useState<string | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef(Date.now());

  const advance = useCallback(() => {
    if (index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    setCard(buildCard());
    setShownAt(Date.now());
    setChosen(null);
  }, [index, recorder]);

  const onChoose = useCallback(
    (choiceId: string) => {
      if (chosen || result) return;
      const ok = choiceId === card.part.id;
      const attempt: Omit<Attempt, "module"> = {
        char: card.part.id,
        ok,
        ms: Date.now() - shownAt,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setChosen(choiceId);
    },
    [card, chosen, recorder, result, shownAt],
  );

  if (result) {
    return (
      <EndCard
        result={result}
        sessionStartMs={sessionStartedAt.current}
        onAgain={() => {
          setIndex(0);
          setCard(buildCard());
          setShownAt(Date.now());
          setChosen(null);
          setResult(null);
          sessionStartedAt.current = Date.now();
        }}
        onBack={() => onNavigate("/play/sailing")}
      />
    );
  }

  const revealed = chosen !== null;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "sailing" : null} onClose={() => setHelpOpen(false)} />

      <p className="small muted">
        Question {index + 1} of {CARDS_PER_SESSION}
      </p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>What's the highlighted part?</h3>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: 8,
            marginBottom: 12,
            background: "var(--bg-elev)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
          }}
        >
          <SailPartsFigure
            highlight={card.part.id}
            showAllLabels={revealed}
            size={260}
          />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {card.choices.map((c) => {
            const isCorrect = c.id === card.part.id;
            const isChosen = c.id === chosen;
            return (
              <button
                key={c.id}
                className="secondary"
                disabled={revealed}
                onClick={() => onChoose(c.id)}
                style={{
                  fontSize: 15,
                  padding: "12px 4px",
                  fontWeight: 600,
                  background:
                    revealed && isCorrect
                      ? "rgba(44, 122, 59, 0.18)"
                      : revealed && isChosen
                        ? "rgba(184, 48, 42, 0.18)"
                        : undefined,
                }}
              >
                {c.name}
              </button>
            );
          })}
        </div>

        {revealed && (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 6,
              background:
                chosen === card.part.id
                  ? "rgba(44, 122, 59, 0.10)"
                  : "rgba(184, 48, 42, 0.10)",
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>
              {chosen === card.part.id
                ? `✓ Correct — ${card.part.name}.`
                : `✗ Not quite — that's the ${card.part.name}.`}
            </p>
            <p className="small" style={{ margin: "8px 0 0" }}>
              <strong>What it is:</strong> {card.part.meaning}
            </p>
            <p className="small" style={{ margin: "6px 0 0" }}>
              <strong>Why you care:</strong> {card.part.use}
            </p>
            <button onClick={advance} style={{ marginTop: 12, width: "100%" }}>
              {index + 1 >= CARDS_PER_SESSION ? "See score →" : "Next →"}
            </button>
          </div>
        )}
      </div>

      <button className="secondary" onClick={() => onNavigate("/play/sailing")}>
        ← Back to Sailing
      </button>
    </>
  );
}

function EndCard({
  result,
  sessionStartMs,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  sessionStartMs: number;
  onAgain: () => void;
  onBack: () => void;
}) {
  const elapsedS = Math.round((Date.now() - sessionStartMs) / 1000);
  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}{" "}
          in {elapsedS}s.
        </p>
      </div>
      <div className="row" style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}>
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Sailing
        </button>
      </div>
    </>
  );
}
